import SingleSelect from "@components/plot/plotConfig/SingleSelect"
import { useAppStructure } from "@redux/features/appStructure/useAppStructure"
import { useNavigate } from "@tanstack/react-router"
import { Button, LoadingCircle } from "@ui-lib/index"
import { CustomerData } from "api"
import { ReactElement, Suspense, useMemo, useState } from "react"
import { ClientCard } from "./ClientCard"

type VesselOptionValue = {
	vesselName: string
	clientName: string
	vesselId: number
}

type VesselOption = {
	value: VesselOptionValue
	label: string
}

function collectVesselOptions(
	clientData: CustomerData,
	clientName: string,
	options: VesselOption[]
) {
	for (const plant of Object.values(clientData.plants)) {
		if (plant == null) {
			continue
		}
		options.push({
			value: {
				vesselName: plant.display_name,
				clientName,
				vesselId: plant.id,
			},
			label: `${plant.display_name} (id: ${plant.id}) - ${clientName}`,
		})
	}

	for (const [subClientName, subClientData] of Object.entries(clientData.clients)) {
		if (subClientData == null) {
			continue
		}
		collectVesselOptions(subClientData, subClientName, options)
	}
}

function ComponentBody({ companyData }: { companyData: CustomerData }): ReactElement {
	const clients = companyData.clients
	const navigate = useNavigate()
	const [selectedVessel, setSelectedVessel] = useState<VesselOption | null>(null)

	const vesselOptions = useMemo(() => {
		const options: VesselOption[] = []
		for (const [clientName, clientData] of Object.entries(clients)) {
			if (clientData == null) {
				continue
			}
			collectVesselOptions(clientData, clientName, options)
		}
		return options.sort((a, b) => a.label.localeCompare(b.label))
	}, [clients])

	const handleVesselChange = (option: VesselOption | null) => {
		setSelectedVessel(option)
	}

	const handleGo = () => {
		if (selectedVessel == null) {
			return
		}
		navigate({
			to: "/clients/$clientName/fleet-overview",
			params: { clientName: selectedVessel.value.clientName },
			search: (prev) => ({ ...prev, vessel: selectedVessel.value.vesselName }),
		})
	}

	return (
		<Suspense fallback={<LoadingCircle containerHeightWithUnit="100vh" />}>
			<div className="m-auto mt-5 w-7/10 pb-5">
				<div className="flex flex-col items-center gap-3 md:flex-row md:items-center md:justify-center">
					<div className="w-full md:max-w-130">
						<SingleSelect
							options={vesselOptions}
							value={selectedVessel}
							onChange={handleVesselChange}
							isClearable={true}
							placeholder="Search for a vessel..."
						/>
					</div>
					<Button
						disabled={selectedVessel == null}
						className="w-47.5 whitespace-nowrap"
						onClick={handleGo}
					>
						<Button.Label>{"Navigate to vessel"}</Button.Label>
					</Button>
				</div>
			</div>
			<div className="m-auto grid w-7/10 grid-cols-[repeat(auto-fit,minmax(300px,1fr))] gap-5 pb-5">
				{Object.keys(clients)
					.sort()
					.map((client) => (
						<ClientCard
							key={`clientCards_${client}`}
							client={client}
							isAllAssets={client.split(":")[1] === "All" ? true : false}
						/>
					))}
			</div>
		</Suspense>
	)
}

export default function ClientsPage(): ReactElement {
	const { data: appStructure } = useAppStructure()

	if (appStructure == null) {
		return <LoadingCircle containerHeightWithUnit="100vh" />
	}

	const companyName = Object.keys(appStructure)[0]

	if (companyName === undefined) {
		return <div> {`Server error: unkown company`}</div>
	}
	const companyData = appStructure[companyName]
	if (companyData === undefined) {
		return <div> {`Server error: unkown company`}</div>
	}

	return <ComponentBody companyData={companyData} />
}
